import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Attorney,
  LinkedDocumentTemplate,
  useDeleteAttorneyMutation,
  useLazyPreDeleteCheckQuery,
} from '@/services/api/attorneysService';

interface Props {
  firmId: string;
  attorney: Pick<Attorney, 'id' | 'first_name' | 'last_name'>;
  onDeleted?: () => void;
}

const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  // V2 category values
  RETAINER: 'Engagement Letter',
  LOR_GENERIC: 'Letter of Representation',
  LOR_FIRST_PARTY: 'First Party Letter of Representation',
  LOR_THIRD_PARTY: 'Third Party Letter of Representation',
  LOR_HEALTH: 'First Party LoR - Health Insurance',
  HIPAA_AUTHORIZATION: 'HIPAA Authorization',
  HIPAA_COVERSHEET: 'HIPAA Coversheet',
  DROP_LETTER: 'Drop Letter',
  JOINT_VENTURE_NOTICE: 'Joint Venture Notice',
  // Legacy values (in case backend hasn't fully migrated this endpoint)
  ENGAGEMENT_LETTER: 'Engagement Letter',
  LETTER_OF_REPRESENTATION: 'Letter of Representation',
  LETTER_OF_PROTECTION: 'Letter of Protection',
  MEDICAL_RECORDS_REQUEST: 'Medical Records Request',
};

const formatCategory = (category: string): string =>
  TEMPLATE_TYPE_LABELS[category] ??
  category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const DeleteAttorneyDialog = ({
  firmId,
  attorney,
  onDeleted,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [linkedTemplates, setLinkedTemplates] = useState<
    LinkedDocumentTemplate[]
  >([]);
  const [checkComplete, setCheckComplete] = useState(false);
  const [deleteAttorney, { isLoading: isDeleting }] =
    useDeleteAttorneyMutation();
  const [triggerPreDeleteCheck, { isFetching: isChecking }] =
    useLazyPreDeleteCheckQuery();

  const handleOpenChange = async (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setCheckComplete(false);
      setLinkedTemplates([]);
      try {
        const result = await triggerPreDeleteCheck({
          firm_id: firmId,
          id: attorney.id,
        }).unwrap();
        setLinkedTemplates(result.linked_templates);
      } catch {
        // If the check fails, still allow deletion
      } finally {
        setCheckComplete(true);
      }
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAttorney({ firm_id: firmId, id: attorney.id }).unwrap();
      toast.success('Attorney deleted');
      setOpen(false);
      if (onDeleted) onDeleted();
    } catch (e: unknown) {
      const err = e as { data?: { detail?: string } };
      const msg = err?.data?.detail || 'Failed to delete attorney';
      toast.error(msg);
    }
  };

  const isLoading = isChecking || !checkComplete;
  const hasLinkedTemplates = linkedTemplates.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Attorney</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <p className="text-sm text-zinc-500">
            Checking for linked templates...
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">
              Are you sure you want to delete{' '}
              <span className="font-medium">
                {attorney.first_name} {attorney.last_name}
              </span>
              ? This action cannot be undone.
            </p>

            {hasLinkedTemplates && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-800">
                  This attorney is linked to {linkedTemplates.length} document{' '}
                  {linkedTemplates.length === 1 ? 'template' : 'templates'}:
                </p>
                <ul className="mt-2 space-y-1">
                  {linkedTemplates.map((t) => (
                    <li
                      key={t.id}
                      className="text-sm text-amber-700 flex items-start gap-2"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                      <span>
                        <span className="font-medium">{t.name}</span>
                        <span className="text-amber-600">
                          {' '}
                          ({formatCategory(t.category)})
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-sm text-amber-700">
                  Deleting this attorney will remove the attorney association
                  from these templates.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading || isDeleting}
          >
            {isDeleting && 'Deleting...'}
            {!isDeleting && hasLinkedTemplates && 'Delete Anyway'}
            {!isDeleting && !hasLinkedTemplates && 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
